/* eslint-disable max-lines */
// Please don't remove the above until this is resolved: https://jira.tools.aws.vodafone.com/browse/UC-31448
import { useCallback, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SimpleGrid from "@vfuk/core-simple-grid/";
import Spacing from "@vfuk/core-spacing";
import Button from "@vfuk/core-button";
import Container from "@vfuk/core-container";
import { User } from "src/interfaces/user";
import { useUserManagementStore } from "src/hooks/stores";
import {
  SuccessModalContent,
  FailureModalContent,
  NoResultsRow,
  Modal,
  FloatingLabelInput,
  SelectInput,
  SelectOption,
  Stack,
  useDebounce,
} from "@ucchub/ui";
import Heading from "@vfuk/core-heading/";
import Paragraph from "@vfuk/core-paragraph/";
import { useRoleManagementStore } from "src/hooks/stores";
import { ValidationSchema, CountryCodeFields } from "src/interfaces";
import type { ButtonProps } from "@vfuk/core-button/dist/Button.types";
import { UserTypes } from "@hub/common-libs";
import { FormFieldType } from "@hub/common-libs/build/types/customerDetails.service.model";
import { SelectInputWithLabelProps } from "@vfuk/core-select-input-with-label/dist/SelectInputWithLabel.types";
import {
  ContactNumberInputBorder,
  CountryCodeInputBorder,
} from "./UserFormStyles";
import useCustomerList from "@ucchub/ui/src/cloudNumbers/hooks/customer/useCustomerList";

interface UserFormProps {
  user?: User;
}
interface UserFormFields {
  firstName: { pattern: { value: RegExp; message: string } };
  lastName: { pattern: { value: RegExp; message: string } };
  email: { pattern: { value: RegExp; message: string } };
  roleId: { pattern: { value: RegExp; message: string } };
  countryCode: { pattern: { value: RegExp; message: string } };
  contactNumber: { pattern: { value: RegExp; message: string } };
  organisation: { pattern: { value: RegExp; message: string } };
}

const UserForm = (props: UserFormProps) => {
  const initialFormFields = {
    firstName: { pattern: { value: new RegExp(""), message: "" } },
    lastName: { pattern: { value: new RegExp(""), message: "" } },
    email: { pattern: { value: new RegExp(""), message: "" } },
    roleId: { pattern: { value: new RegExp(""), message: "" } },
    countryCode: { pattern: { value: new RegExp(""), message: "" } },
    contactNumber: { pattern: { value: new RegExp(""), message: "" } },
    organisation: { pattern: { value: new RegExp(""), message: "" } },
  };
  const { t } = useTranslation();
  const user = props?.user;
  const isAddMode = user === undefined;
  const formHandler = useForm({ mode: "onTouched" });
  const { handleSubmit, control, setValue, formState, getValues } = formHandler;

  const navigate = useNavigate();
  const addUser = useUserManagementStore((state) => state.addUser.call);
  const updateUser = useUserManagementStore((state) => state.updateUser.call);
  const getUserValidationSchema = useUserManagementStore(
    (state) => state.fetchUserValidationSchema.call
  );
  const schemaLoadError = useUserManagementStore(
    (state) => state.fetchUserValidationSchema.error
  );
  const userSchema = useUserManagementStore(
    (state) => state.userValidationSchema
  );
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showFailureModal, setShowFailureModal] = useState(false);
  const [modalEmail, setModalEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({
    headerMessage: "",
    subHeaderMessage: "",
  });
  const [formFieldRules, setFormFieldsRules] =
    useState<UserFormFields>(initialFormFields);
  const [disableBtn, setDisableBtn] =
    useState<ButtonProps["state"]>("disabled");
  const { call: getRoles } = useRoleManagementStore((state) => state.getRoles);

  const [roles, setRoles] = useState<SelectOption[] | []>([]);
  const [organisationSearchValue, setOrganisationSearchValue] = useState("");
  const { customerList, fetchCustomerList, clearCustomerList } =
    useCustomerList();
  const debouncedSearchValue = useDebounce<string>(
    organisationSearchValue,
    200
  );
  const [organisations, setOrganisations] = useState<SelectOption[] | []>([]);

  const [countriesOptions, setCountriesOptions] = useState<CountryCodeFields[]>(
    []
  );
  const countriesList: [] = useUserManagementStore(
    (state) => state.countiresDropdownList
  );

  const clearUsers = useUserManagementStore((state) => state.clearUsers);

  const [isCountryCodeAdded, setCountryCodeAdded] = useState(false);
  const { errors } = formState;

  const countryCodeChange = (value: string | undefined) => {
    if (!value) {
      setValue("contactNumber", "");
      setCountryCodeAdded(false);
      return;
    }
    setCountryCodeAdded(true);
  };
  const [countryValue, setCountryValue] = useState<SelectOption | null>(null);
  const [existingCountryValue, setExistingCountryValue] = useState<
    string | undefined
  >("");

  const onSubmit = handleSubmit((data) => {
    const userData = {
      ...data,
      // Select inputs need to store the whole object,  but we only need the value   .
      orgId: data.orgId.value,
      roleId: data.roleId.value,
      countryCode: data.countryCode?.value || undefined,
    } as unknown as UserTypes.CreateUserData;

    setMessage({ headerMessage: "", subHeaderMessage: "" });
    setLoading(true);
    setModalEmail(data.email); // Email used to show in the Modal

    if (isAddMode) {
      addUser(userData)
        .then((res) => {
          setMessage({
            headerMessage: res.message || "",
            subHeaderMessage: res.data.message || "",
          });
          setTimeout(() => {
            setShowSuccessModal(true);
          });
        })
        .catch((error) => {
          setMessage({
            headerMessage: error.data.message || "",
            subHeaderMessage:
              (error.data.data && error.data.data.message) || "",
          });
          setShowFailureModal(true);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      // Update user
      const collectDirtyFields = Object.keys(formState.touchedFields).map(
        (x) => ({
          [x]: getValues(x),
        })
      );
      const updatedFieldsObject = Object.assign({}, ...collectDirtyFields);
      updatedFieldsObject.email = data.email;
      if (
        existingCountryValue !== countryValue?.value &&
        countryValue?.value === "Country code"
      ) {
        delete updatedFieldsObject.contactNumber;
        delete updatedFieldsObject.countryCode;
      } else if (
        existingCountryValue !== countryValue?.value &&
        countryValue?.value !== "Country code"
      ) {
        updatedFieldsObject.countryCode = countryValue?.value;
      }
      updateUser({
        ...updatedFieldsObject,
        roleId: getValues("roleId").value,
        userId: user.userId,
      })
        .then((res) => {
          setMessage({
            headerMessage: res.message || "",
            subHeaderMessage: res.data.message || "",
          });
          setTimeout(() => {
            setShowSuccessModal(true);
          });
        })
        .catch((error) => {
          setMessage({
            headerMessage: error.data.message || "",
            subHeaderMessage:
              (error.data.data && error.data.data.message) || "",
          });
          setShowFailureModal(true);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  });
  useEffect(() => {
    clearCustomerList();
    fetchCustomerList({ search: debouncedSearchValue, page: 0 });
  }, [debouncedSearchValue]);

  useEffect(() => {
    if (customerList !== undefined) {
      const activeOrganisation = customerList.filter(
        (organisation: { status?: string }) => organisation.status === "Active"
      );
      setOrganisations(
        activeOrganisation.map(
          (organisation: { name: string; id: string }) => ({
            label: organisation.name,
            value: organisation.id,
          })
        )
      );
    }
  }, [customerList, setOrganisations]);

  useEffect(() => {
    getUserValidationSchema();
  }, [getUserValidationSchema]);

  useEffect(() => {
    const res = countriesList;
    const countries = JSON.parse(JSON.stringify(res));

    if (countries.length > 0) {
      setCountriesOptions(
        countries.map(
          (country: { displayText: string; value: string; icon: string }) => ({
            value: country.value ? country.value : "",
            icon: "/core/img/icons/flags/" + country.icon + ".svg",
            label: (
              <Stack style={{ width: "100%" }} justifyContent="space-between">
                <span className="countryNameHidden">{country.displayText}</span>
                <span>{country.value}</span>
              </Stack>
            ),
          })
        )
      );
    }
  }, [countriesList, userSchema]);

  const fetchRoles = useCallback(async () => {
    const res = await getRoles(false, true);
    const roles = JSON.parse(JSON.stringify(res));

    if (roles.length > 0) {
      setRoles(
        roles.map((role: { roleFriendlyName: string; roleId: string }) => ({
          label: role.roleFriendlyName,
          value: role.roleId,
        }))
      );
      if (isAddMode) {
        setValue("roleId", roles[0].roleId, {}); //Setting the default value
      }
    }
  }, [getRoles, isAddMode, setValue]);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  useEffect(() => {
    const formFields = [
      "firstName",
      "lastName",
      "email",
      "roleId",
      "organisation",
      "contactNumber",
      "countryCode",
    ];
    const rules: UserFormFields | [] = [];

    formFields.forEach((element: FormFieldType["fieldName"]) => {
      const fieldExists = userSchema.find(
        (item: FormFieldType) => item.fieldName === element
      ) as unknown as FormFieldType;
      if (fieldExists) {
        let fieldRules = {};
        if (isCountryCodeAdded && fieldExists.fieldName === "contactNumber") {
          // checking the countrycode is selected from the dropdown and setting the rules for contact number
          setDisableBtn("disabled");
          fieldRules = {
            required: {
              value: isCountryCodeAdded || false,
              message: isCountryCodeAdded
                ? fieldExists.requiredErrorMessage
                : "",
            },
          };
        } else if (
          !isCountryCodeAdded &&
          fieldExists.fieldName === "contactNumber"
        ) {
          fieldRules = {
            required: {
              value: false,
              message: "",
            },
          };
        } else {
          fieldRules = {
            required: {
              value: fieldExists.required || false,
              message:
                fieldExists.requiredErrorMessage ||
                element + " is not required",
            },
          };
        }
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore: Not sure what this code does, but produces a TS Error
        rules[element] = fieldRules;
      } else {
        const fieldRules = {
          required: {
            value: false,
            message: element + " is not required",
          },
        };
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore: Not sure what this code does, but produces a TS Error
        rules[element] = fieldRules;
      }
    });
    setFormFieldsRules(rules as unknown as UserFormFields);
  }, [isCountryCodeAdded, userSchema]);

  useEffect(() => {
    // Set the existing user's values if NOT in Add (Create) Mode
    if (!isAddMode) {
      setValue("firstName", user.firstName);
      setValue("lastName", user.lastName);
      setValue("email", user.email);
      setValue("countryCode", user.countryCode);
      setValue("contactNumber", user.contactNumber);
      setValue("orgId", {
        value: user.organisation?.id,
        label: user.organisation?.orgName,
      });
      // Find the user's countryCode in the list of countries and set the value
      const country = countriesOptions.find(
        (country) => country.value === user.countryCode
      );

      // Set the country value & format the data so the SelectInput accepts it
      if (country)
        setCountryValue({
          value: country.value ? country.value : "",
          icon: "/core/img/icons/flags/" + country.icon + ".svg",
          label: (
            <Stack style={{ width: "100%" }} justifyContent="space-between">
              <span className="countryNameHidden">{country.displayText}</span>
              <span>{country.value}</span>
            </Stack>
          ),
        });

      setExistingCountryValue(user.countryCode);
      if (user.countryCode !== undefined) setCountryCodeAdded(true);
    }
  }, [user, isAddMode, setValue, countriesOptions]);

  useEffect(() => {
    if (formState.isDirty) {
      if (!isCountryCodeAdded) {
        delete formState.errors.contactNumber;
      }
      if (Object.keys(formState.errors).length === 0) setDisableBtn(undefined);
      else setDisableBtn("disabled");
    } else {
      setDisableBtn("disabled");
    }
  }, [errors, formState, isCountryCodeAdded, schemaLoadError]);

  // Once the roles & organisations have been fetched,
  // re-format and set the values for the dropdowns
  useEffect(() => {
    if (!user) return;

    // Set the role
    const role = roles.find((role) => role.value === user.role.id);
    if (role) setValue("roleId", role);
  }, [roles, user, setValue, customerList]);

  //handler for adding pattern rule out of array on change #AMIT
  const handlerForRulePatterns = (
    fieldName: keyof UserFormFields,
    value: string
  ) => {
    let shouldSkip = false;
    const fieldExists: FormFieldType | undefined = userSchema.find(
      (item: { fieldName: string }) => item.fieldName === fieldName
    ) as unknown as FormFieldType;
    if (fieldExists) {
      let fieldsValidation: ValidationSchema[] = [];
      fieldsValidation = fieldExists.validation;
      if (fieldsValidation && fieldsValidation.length > 0) {
        fieldsValidation.forEach((element) => {
          if (element.pattern) {
            if (shouldSkip) {
              return;
            }
            setDisableBtn(undefined);
            if (!new RegExp(element.pattern).test(value)) {
              setDisableBtn("disabled");
              shouldSkip = true;

              formFieldRules[fieldName].pattern = {
                value: new RegExp(element.pattern),
                message: element.errorMessage,
              };
            }
          }
        });
      }
    }
  };

  return (
    <>
      <Modal
        srName={t("shared:users:user_successfully_created")}
        isOpen={showSuccessModal}
        size={2}
        onClose={() => {
          setShowSuccessModal(false);
          clearUsers();
          navigate("/users");
        }}
      >
        <SuccessModalContent
          heading={
            message.headerMessage ||
            t("shared:users:confirmation:user_successfully_created.message")
          }
          body={
            message.subHeaderMessage ||
            t("shared:users:confirmation:user_successfully_created.message1", {
              text: modalEmail,
            })
          }
          confirmText={"Back to Portal users"}
          onConfirm={() => {
            clearUsers();
            navigate("/users");
          }}
        />
      </Modal>
      <Modal
        srName={t("shared:users:failed_to_create_user")}
        isOpen={showFailureModal}
        size={2}
        onClose={() => setShowFailureModal(false)}
      >
        <FailureModalContent
          heading=""
          message={
            message.headerMessage ||
            t("shared:users:failed_to_create_user", { text: modalEmail })
          }
          body={message.subHeaderMessage || ""}
          closeMessage={"Close"}
          onClose={() => setShowFailureModal(false)}
          loading={loading}
        />
      </Modal>
      {schemaLoadError !== "" && (
        <NoResultsRow text={t("shared:users:cannot_create_user")} />
      )}
      {schemaLoadError === "" && (
        <form onSubmit={onSubmit}>
          <Container>
            <Paragraph appearance="secondary">
              ({t("shared:users:validation_default_message")})
            </Paragraph>
            <Heading level={4} weight={3}>
              User details
            </Heading>
            <SimpleGrid
              columns={{
                sm: 1,
                md: 2,
              }}
              spacing={3}
            >
              <Controller
                name="firstName"
                control={control}
                rules={formFieldRules.firstName}
                defaultValue=""
                render={({ field }) => (
                  <FloatingLabelInput
                    label="First name"
                    error={
                      errors.firstName
                        ? (errors.firstName.message as string)
                        : undefined
                    }
                    type="text"
                    id="firstName"
                    state={errors.firstName ? "error" : undefined}
                    {...field}
                    onChange={(e) => {
                      field.onChange(e);
                      handlerForRulePatterns("firstName", e.target.value);
                    }}
                  />
                )}
              />
              <Controller
                name="lastName"
                control={control}
                rules={formFieldRules.lastName}
                defaultValue=""
                render={({ field }) => (
                  <FloatingLabelInput
                    label="Last name"
                    error={
                      errors.lastName
                        ? (errors.lastName.message as string)
                        : undefined
                    }
                    type="text"
                    id="lastName"
                    state={errors.lastName ? "error" : undefined}
                    {...field}
                    onChange={(e) => {
                      field.onChange(e);
                      handlerForRulePatterns("lastName", e.target.value);
                    }}
                  />
                )}
              />
            </SimpleGrid>

            <Spacing
              spacingLevel={{
                top: 4,
                bottom: 4,
              }}
            >
              <Controller
                name="email"
                control={control}
                rules={formFieldRules.email}
                defaultValue=""
                render={({ field }) => (
                  <FloatingLabelInput
                    label="Email ID"
                    error={
                      errors.email
                        ? (errors.email.message as string)
                        : undefined
                    }
                    type="email"
                    id="email"
                    state={
                      isAddMode
                        ? errors.email
                          ? "error"
                          : undefined
                        : "disabled"
                    }
                    {...field}
                    onChange={(e) => {
                      field.onChange(e);
                      handlerForRulePatterns("email", e.target.value);
                    }}
                  />
                )}
              />
            </Spacing>
            {countriesOptions && (
              <SimpleGrid
                columns={{
                  sm: 1,
                  md: 2,
                }}
                layout={{
                  sm: [12, 12],
                  lg: [5, 7],
                }}
              >
                <Controller
                  name="countryCode"
                  control={control}
                  rules={formFieldRules.countryCode}
                  defaultValue=""
                  render={({ field }) => (
                    <CountryCodeInputBorder>
                      <SelectInput
                        spaceForError={false}
                        label="Country code"
                        error={
                          errors.countryCode
                            ? (errors.countryCode.message as string)
                            : undefined
                        }
                        id="countryCode"
                        value={countryValue || ""}
                        options={
                          countriesOptions as unknown as SelectInputWithLabelProps["selectInput"]["options"]
                        }
                        onChange={(e: SelectOption) => {
                          countryCodeChange(e.value);
                          setCountryValue(e);
                          field.onChange(e);
                        }}
                        state={errors.countryCode ? "error" : undefined}
                      />
                    </CountryCodeInputBorder>
                  )}
                />
                <Controller
                  name="contactNumber"
                  control={control}
                  rules={formFieldRules.contactNumber}
                  defaultValue=""
                  render={({ field }) => (
                    <ContactNumberInputBorder>
                      <FloatingLabelInput
                        label={
                          isCountryCodeAdded
                            ? "Contact number"
                            : "Contact number (optional)"
                        } //showing the label as optional if the country code was not selected , if its selected then its required field
                        error={
                          errors.contactNumber
                            ? (errors.contactNumber.message as string)
                            : undefined
                        }
                        type="number"
                        id="contactNumber"
                        state={
                          (errors.contactNumber ? "error" : undefined) ||
                          (!isCountryCodeAdded ? "disabled" : undefined)
                        }
                        {...field}
                        onChange={(e) => {
                          field.onChange(e);
                          handlerForRulePatterns(
                            "contactNumber",
                            e.target.value
                          );
                        }}
                      />
                    </ContactNumberInputBorder>
                  )}
                />
              </SimpleGrid>
            )}

            <Spacing
              spacingLevel={{
                top: 4,
                bottom: 4,
              }}
            >
              {!isAddMode && (
                <Controller
                  name="orgId"
                  control={control}
                  rules={formFieldRules.organisation}
                  defaultValue=""
                  render={({ field }) => (
                    <SelectInput
                      id="orgId"
                      label="Organisation"
                      isDisabled
                      options={getValues("orgId")}
                      {...field}
                    />
                  )}
                />
              )}
              {isAddMode && (
                <Controller
                  name="orgId"
                  control={control}
                  rules={formFieldRules.organisation}
                  defaultValue=""
                  render={({ field }) => (
                    <SelectInput
                      id="orgId"
                      label="Organisation"
                      state={
                        isAddMode
                          ? errors.organisation
                            ? "error"
                            : undefined
                          : "disabled"
                      }
                      error={
                        errors.orgId
                          ? (errors.orgId.message as string)
                          : undefined
                      }
                      spaceForError
                      options={organisations}
                      {...field}
                      onInputChange={(org: string) => {
                        setOrganisationSearchValue(org);
                      }}
                    />
                  )}
                />
              )}
            </Spacing>
            <Spacing
              spacingLevel={{
                top: 4,
                bottom: 4,
              }}
            >
              <Controller
                name="roleId"
                control={control}
                rules={formFieldRules.roleId}
                defaultValue="role_developer"
                render={({ field }) => (
                  <SelectInput
                    id="role"
                    label="Role"
                    state={errors.role ? "error" : undefined}
                    error={
                      errors.roleId
                        ? (errors.roleId.message as string)
                        : undefined
                    }
                    spaceForError
                    options={roles}
                    {...field}
                  />
                )}
              />
            </Spacing>
            <SimpleGrid
              columns={{
                sm: 1,
                md: 2,
              }}
              spacing={3}
            >
              <Button
                width="full"
                text="Cancel"
                appearance="alt1"
                onClick={() =>
                  isAddMode
                    ? navigate("/users/")
                    : navigate("/users/" + user?.userId)
                }
              />
              <Button
                width="full"
                onClick={onSubmit}
                text={isAddMode ? "Add user" : "Save changes"}
                appearance={disableBtn ? "secondary" : "primary"}
                loading={loading}
                state={disableBtn || undefined}
              />
            </SimpleGrid>
          </Container>
        </form>
      )}
    </>
  );
};

export default UserForm;
